import React, { FC, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Select from 'shared-components/atom/select';
import WindowedSelect from 'react-windowed-select';
import {
  googleShoppingLanguageNameOptions,
  googleShopingCountriesOptions,
  googleSearchRestrictedCountOptions,
  googleShoppingSortbyOptions,
} from '../../constants';
import { useAutomationGlobalMethods } from '../../hooks';
import Input from 'shared-components/atom/input';
import { AutomationSettingsFactoryTypesProps } from '../../types';
import Alert from 'shared-components/atom/alert-message';
// import { cacheFunction } from 'helpers';
import './styles.scss';

const output = [
  '_google_product_shopping_rank_group',
  '_google_product_shopping_rank_absolute',
  '_google_product_shopping_position',
  '_google_product_shopping_xpath',
  '_google_product_shopping_domain',
  '_google_product_shopping_title',
  '_google_product_shopping_description',
  '_google_product_shopping_url',
  '_google_product_shopping_shopping_url',
  '_google_product_shopping_tags',
  '_google_product_shopping_price',
  '_google_product_shopping_currency',
  '_google_product_shopping_product_id',
  '_google_product_shopping_seller',
  '_google_product_shopping_reviews_count',
  '_google_product_shopping_is_best_match',
  '_google_product_shopping_additional_specifications_eto',
  '_google_product_shopping_product_rating_type',
  '_google_product_shopping_product_rating_position',
  '_google_product_shopping_product_rating_value',
  '_google_product_shopping_product_rating_votes_count',
  '_google_product_shopping_shop_rating',
  '_google_product_shopping_product_images',
  '_google_product_shopping_delivery_message',
  '_google_product_shopping_delivery_price_current',
  '_google_product_shopping_delivery_price_regular',
  '_google_product_shopping_delivery_price_max_value',
  '_google_product_shopping_delivery_price_currency',
  '_google_product_shopping_delivery_is_price_range',
  '_google_product_shopping_delivery_price_displayed_price',
];

const AmazonScraperSettings: FC<AutomationSettingsFactoryTypesProps> = ({
  automationSendSettings,
  automationIndex,
  selectedRecipes,
  setAutomationSendSettings,
  setCompletedFields,
  id,
  isSettingsChange,
  setIsDirty,
  automationOutputTypes,
  automationInputTypes,
}) => {
  const [fieldList, setFieldList] = useState<any>([]);
  const [clicked, setClicked] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedMaxSearchCount, setSelectedMaxSearchCount] = useState(googleSearchRestrictedCountOptions[0]);
  const [countryText, setCountryText] = useState({ label: 'United States', value: 2840 });
  const [languageText, setLanguageText] = useState({ label: 'English', value: 'en' });
  const [selectedSource, setSelectedSource] = useState<any>([]);
  const [saveFrom, setSaveFrom] = useState(1);
  const [saveTo, setSaveTo] = useState(100);
  const [errorMessage, setErrorMessage] = useState('');
  const [minPrice, setMinPrice] = useState<number | null>(null);
  const [maxPrice, setMaxPrice] = useState<number | null>(null);
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<any>(null);
  const [selectedSortBy, setSelectedSortBy] = useState<any>(null);
  const zeroIndexOutput: string[] = automationIndex === 0 ? ['_google_product_shopping_keyword'] : [];
  const [fieldListWithTypes, setFieldListWithTypes] = useState([]);

  const {
    liftingSettings,
    getDynamicAndIntegrationsSettings,
    getPreviousAutomationOutputOrPreviousRecipKeys,
    getPreviousAutomationOutputOrPreviousRecipKeysWithTypes,
    restoreSelectedSource,
  } = useAutomationGlobalMethods({
    automationSendSettings,
    setAutomationSendSettings,
    selectedRecipes,
    automationIndex,
    id,
  });

  const checkFields = () => {
    if (errorMessage || !saveFrom || !saveTo || !selectedMaxSearchCount) {
      return false;
    }
    if (fieldList?.length === 0 ? !searchKeyword : Array.isArray(selectedSource) ? !selectedSource?.length : !selectedSource) return false;
    return true;
  };

  useEffect(() => {
    const { dynamic_settings } = getDynamicAndIntegrationsSettings();
    if (dynamic_settings) {
      const { keys } = dynamic_settings;

      const { settings } = keys[0];

      const depth = settings.depth;
      const inputValue = keys[0].inputValue;
      const location_name = settings.location_name;
      const save_from = settings.save_from;
      const save_to = settings.save_to;
      const sort_by = settings.sort_by;
      const price_min = settings.price_min;
      const price_max = settings.price_max;
      const language_name = settings.language_name;

      setSearchKeyword(inputValue);
      const findedCount = googleSearchRestrictedCountOptions.find(item => item.value === depth);
      findedCount && setSelectedMaxSearchCount(findedCount);
      const findedCountry: any = googleShopingCountriesOptions.find((item: any) => item.label === location_name);
      const findedSortBy = googleShoppingSortbyOptions.find(item => item.value === sort_by);
      const findedSelectedLangCode = googleShoppingLanguageNameOptions.find(
        (item: { label: string }) => item.label === language_name,
      );

      findedCountry && setCountryText(findedCountry);
      findedSortBy && setSelectedSortBy(findedSortBy);
      // findedSelectedLangCode && setSelectedLanguageCode(findedSelectedLangCode);
      findedSelectedLangCode && setLanguageText(findedSelectedLangCode);
      //there may be zero here, since initially these fields were not
      setSaveFrom(save_from === undefined ? 1 : save_from);
      setSaveTo(save_to === undefined ? 100 : save_to);
      setMinPrice(price_min);
      setMaxPrice(price_max);
    }
  }, []);

  //recipe
  useEffect(() => {
    // if (fieldList.length > 0) return;
    if (automationIndex === 0) return;
    let options = getPreviousAutomationOutputOrPreviousRecipKeys();
    const optionsWithTypes = getPreviousAutomationOutputOrPreviousRecipKeysWithTypes();

    const { dynamic_settings } = getDynamicAndIntegrationsSettings();
    if (dynamic_settings) {
      const key = dynamic_settings.keys[0].key;
      restoreSelectedSource({ key: key, fieldList: options, setSelectedSource: setSelectedSource });
    }
    // setFieldList(options);
    const filteredOptions =
      automationInputTypes && !automationInputTypes.includes('any')
        ? options.filter(
            (item: any) =>
              (optionsWithTypes &&
                optionsWithTypes[item.value] &&
                (automationInputTypes.includes(optionsWithTypes[item.value]) ||
                  optionsWithTypes[item.value] === 'any')) ||
              !(optionsWithTypes && optionsWithTypes[item.value]),
          )
        : [...options];

    setFieldList(filteredOptions);
    setFieldListWithTypes(optionsWithTypes);
  }, [isSettingsChange]);

  useEffect(() => {
    if (fieldList?.length === 1) {
      setSelectedSource(fieldList[0]);
      setIsDirty();
      setClicked(true);
    }
  }, [fieldList]);

  useEffect(() => {
    // if (fieldList.length > 0) return;
    if (automationIndex === 0) return;
    let options = getPreviousAutomationOutputOrPreviousRecipKeys();
    const optionsWithTypes = getPreviousAutomationOutputOrPreviousRecipKeysWithTypes();
    const { dynamic_settings } = getDynamicAndIntegrationsSettings();

    const filteredOptions =
      automationInputTypes && !automationInputTypes.includes('any')
        ? options.filter(
            (item: any) =>
              (optionsWithTypes &&
                optionsWithTypes[item.value] &&
                (automationInputTypes.includes(optionsWithTypes[item.value]) ||
                  optionsWithTypes[item.value] === 'any')) ||
              !(optionsWithTypes && optionsWithTypes[item.value]),
          )
        : [...options];
    let changed = false;
    filteredOptions.map((item: any) => !fieldList.find((sub: any) => sub.value === item.value) && (changed = true));
    const update = filteredOptions.length !== fieldList.length || changed;
    if (filteredOptions.length === 0) {
      setSelectedSource(null);
      setClicked(true);
    }
    if (update) {
      const selOptionFound = filteredOptions.find((item: any) => dynamic_settings?.keys?.[0]?.key === item.value);
      setSelectedSource(!selOptionFound ? null : selOptionFound);
      setClicked(true);
      setFieldList(filteredOptions);
      setFieldListWithTypes(optionsWithTypes);
    }
  }, [automationSendSettings]);

  useEffect(() => {
    if (fieldList?.length) {
      if (clicked) return;
      const jsonParse = automationOutputTypes ? JSON.parse(automationOutputTypes) : {};
      liftingSettings({
        outputKeysData: [...fieldList.map((item: { value: any }) => item.value), ...zeroIndexOutput, ...output],
        outputKeysDataWithTypes: { ...fieldListWithTypes, ...jsonParse },
      });
    }
  }, [fieldList, fieldListWithTypes, automationOutputTypes]);

  // //memoize
  // const handleAmazonSearchAdressesOptions = (countryText: string) => {
  //   return googleShopingCountriesOptions.find((item: any) => item.label === countryText);
  // };
  // const getMemoizedGoogleShopingLocationOption = useRef(cacheFunction(handleAmazonSearchAdressesOptions));

  // const handleAmazonLanguageCodeOptions = (languageText: string) => {
  //   return googleShoppingLanguageNameOptions.find(
  //     (item: { label: string; value: string }) => item.label === languageText,
  //   );
  // };
  // const getMemoizedGoogleShopingLanguageOption = useRef(cacheFunction(handleAmazonLanguageCodeOptions));

  useEffect(() => {
    if (!clicked) return;
    const isCompleted = checkFields();
    // setClicked(false);

    const hashId: any = new Date().getTime().toString();
    if (!countryText || !languageText) {
      setCompletedFields({ index: automationIndex, checked: false });
    } else {
      setCompletedFields({ index: automationIndex, checked: isCompleted });
    }

    let localSettings = {
      keys: [
        {
          inputValue: fieldList.length === 0 && automationIndex === 0 ? searchKeyword.trim() : null,
          key: fieldList.length && selectedSource ? selectedSource.value : null,
          settings: {
            depth: selectedMaxSearchCount ? selectedMaxSearchCount.value : null,
            sort_by: selectedSortBy ? selectedSortBy.value : null,
            location_name: countryText ? countryText.label : null,
            save_from: saveFrom,
            save_to: saveTo,
            price_min: minPrice ? +minPrice : null,
            price_max: maxPrice ? +maxPrice : null,
            language_name: languageText ? languageText.label : null,
            uniqueHash: hashId,
          },
        },
      ],
    };
    const jsonParse = automationOutputTypes ? JSON.parse(automationOutputTypes) : {};
    liftingSettings({
      dynamicSettings: localSettings,
      outputKeysData: [...fieldList.map((item: { value: any }) => item.value), ...zeroIndexOutput, ...output],
        outputKeysDataWithTypes: { ...fieldListWithTypes, ...jsonParse },
    });
  }, [
    searchKeyword,
    selectedSource,
    selectedMaxSearchCount,
    countryText,
    saveTo,
    errorMessage,
    saveFrom,
    minPrice,
    maxPrice,
    // selectedLanguageCode,
    selectedSortBy,
    languageText,
    automationIndex,
  ]);

  const clearErrorMsg = () => {
    setTimeout(() => {
      setErrorMessage('');
    }, 3000);
  };

  const handleSaveFrom = (e: { target: { value: any } }) => {
    let val = e.target.value;
    if (isNaN(val)) {
      setErrorMessage('Please use only numbers');
      clearErrorMsg();
      return;
    }
    if (selectedMaxSearchCount.value <= +val) {
      setErrorMessage('"Save from" value can`t be greater than or equal to "Search result" value');
      clearErrorMsg();
      return;
    }
    if (saveTo <= +val) {
      setErrorMessage('Save from" value can`t be greater than or equal to "Save to result" value');
      clearErrorMsg();
      return;
    }
    setSaveFrom(+val);
    errorMessage && setErrorMessage('');
    !clicked && setClicked(true);
    setIsDirty();
  };

  const clearErrorTimeout = () => {
    setTimeout(() => {
      setErrorMessage('');
    }, 5000);
  };

  const handleSaveTo = (e: { target: { value: any } }) => {
    let val = e.target.value;
    if (isNaN(val)) {
      setErrorMessage('Please use only numbers');
      clearErrorMsg();
      return;
    }
    if (selectedMaxSearchCount.value < +val) {
      setErrorMessage('"Save to" value can`t be greater than "Search result" value');
      clearErrorMsg();
      return;
    }
    setSaveTo(+val);
    errorMessage && setErrorMessage('');
    !clicked && setClicked(true);
    setIsDirty();
  };

  return (
    <div className="google-automation-settings">
      <Row className="first-row">
        {fieldList.length > 0 || automationIndex > 0 ? (
          <Col lg={6} className="mb-4">
            <span className="label secondary-color required">Keyword</span>
            <Select
              options={fieldList}
              value={Array.isArray(selectedSource) ? selectedSource[0] : selectedSource}
              placeholder={'Select source'}
              onChange={(selectedItem: any) => {
                setSelectedSource(selectedItem);
                !clicked && setClicked(true);
                setIsDirty();
              }}
            />
          </Col>
        ) : (
          <Col lg={6} className="mb-4">
            <span className="label secondary-color required">Keyword</span>
            <Input
              value={searchKeyword}
              onChange={e => {
                setSearchKeyword(e.target.value);
                !clicked && setClicked(true);
                setIsDirty();
              }}
            />
          </Col>
        )}
        <Col xl={6} className="search-fields">
          <Row>
            <Col lg={4} xl={4} className="mb-4">
              <span className="label secondary-color required">Search results</span>
              <Select
                fullWidth
                options={googleSearchRestrictedCountOptions}
                value={selectedMaxSearchCount}
                placeholder={'Select column'}
                onChange={(selectedItem: any) => {
                  setSelectedMaxSearchCount(selectedItem);
                  setSaveFrom(1);
                  setSaveTo(selectedItem.value);
                  !clicked && setClicked(true);
                  setIsDirty();
                }}
              />
            </Col>
            <Col lg={4} xl={4} className="mb-4 results-from-to">
              <h5 className="pointer-title">From</h5>
              <div>
                <span className="label secondary-color required" style={{ whiteSpace: 'nowrap' }}>
                  Save results
                </span>
                <Input value={saveFrom} onChange={handleSaveFrom} />
              </div>
            </Col>
            <Col lg={4} xl={4} className="mb-4 results-from-to">
              <h5 className="pointer-title">To</h5>
              <div>
                <span className="label secondary-color" style={{ visibility: 'hidden' }}>
                  to
                </span>
                <Input value={saveTo} onChange={handleSaveTo} />
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col lg={6} className="mb-4">
          <span className="label secondary-color required">Language</span>
          <WindowedSelect
            options={googleShoppingLanguageNameOptions}
            onChange={(selectedOption: any) => {
              if (selectedOption && languageText && selectedOption.value === languageText.value) return;
              setLanguageText(selectedOption);
              !clicked && setClicked(true);
              setIsDirty();
            }}
            value={languageText}
          />
        </Col>
        <Col lg={6} className="mb-4">
          <span className="label secondary-color required">Country</span>
          <WindowedSelect
            options={googleShopingCountriesOptions}
            onChange={(selectedOption: any) => {
              if (selectedOption && countryText && selectedOption.value === countryText.value) return;
              setCountryText(selectedOption);
              !clicked && setClicked(true);
              setIsDirty();
            }}
            value={countryText}
          />
        </Col>
      </Row>
      <Row>
        <Col lg={6} className="mb-4">
          <span className="label secondary-color">Sort by</span>
          <Select
            options={googleShoppingSortbyOptions}
            value={selectedSortBy}
            placeholder={'Select source'}
            onChange={(selectedItem: any) => {
              setSelectedSortBy(selectedItem);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
        <Col lg={3} className="mb-4">
          <span className="label secondary-color">Min price</span>
          <Input
            value={minPrice || ''}
            onChange={e => {
              let val = e.target.value;
              if (isNaN(val)) {
                setErrorMessage('min price must be number');
                clearErrorTimeout();
                return;
              }
              if (maxPrice && +val > +maxPrice) {
                setErrorMessage('min price must be less than max price');
                clearErrorTimeout();
                return;
              }
              errorMessage && setErrorMessage('');
              setMinPrice(e.target.value);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
        <Col lg={3} className="mb-4">
          <span className="label secondary-color">Max price</span>
          <Input
            value={maxPrice || ''}
            onChange={e => {
              let val = e.target.value;
              if (isNaN(val)) {
                setErrorMessage('max price must be number');
                clearErrorTimeout();
                return;
              }
              if (minPrice && +val < +minPrice) {
                setErrorMessage('max price must be greater than min price');
              } else {
                errorMessage && setErrorMessage('');
              }
              setMaxPrice(e.target.value);
              !clicked && setClicked(true);
              setIsDirty();
            }}
          />
        </Col>
      </Row>
      {errorMessage && (
        <Row className="mb-4">
          <Col md={12}>
            <Alert type="error" className="create-workflow">
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default React.memo(AmazonScraperSettings);
